import React, { useState, useEffect } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SearchBar from "../../components/SearchBar";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import SwitchToggle from "../../components/SwitchToggle";
import AddSubCategory from "../../Modals/addSubCategory";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";
import { useGetCategoryDetailsQuery, useUpdateCategoryMutation, useDeleteCategoryMutation } from "../../service/features/api";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { toast } from "react-toastify";

import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";

const ManageSubCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Categories Management", "isEdit");

  const categoryId = location.state?.categoryId;

  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);
  const [page, setPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  const { data: categoryData, isLoading } = useGetCategoryDetailsQuery(categoryId, {
    skip: !categoryId,
  });

  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();

  useEffect(() => {
    if (!categoryId) {
      toast.error("No category selected");
      navigate("/manage-categories");
    }
  }, [categoryId, navigate]);

  const parentCategory = categoryData?.data?.name || "Category";
  const allSubCategories = categoryData?.data?.subCategories || [];

  // Filter and Pagination Logic
  const filteredSubCategories = allSubCategories.filter((sub: any) =>
    sub.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubCategories.length / ROWS_PER_PAGE);
  const paginatedSubCategories = filteredSubCategories.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  const handleToggle = async (subCategory: any, status: boolean) => {
    const vType = subCategory.type;
    const payload: any = {
      id: subCategory._id,
      name: subCategory.name,
      isActive: status,
      parentId: categoryId,
      type: Array.isArray(vType) ? vType : (vType ? [vType] : [])
    };

    if (subCategory.icon) payload.icon = subCategory.icon;
    if (subCategory.image) payload.image = subCategory.image;

    try {
      await updateCategory(payload).unwrap();
      toast.success("Status updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  const handleEdit = (subCategory: any) => {
    setSelectedSubCategory(subCategory);
    setOpenModal(true);
  };

  const handleDeleteClick = (subCategory: any) => {
    setSelectedSubCategory(subCategory);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSubCategory?._id) return;
    try {
      await deleteCategory(selectedSubCategory._id).unwrap();
      toast.success("Sub-Category deleted successfully");
      setOpenDeleteModal(false);
      // If last item on page deleted, go back a page
      if (paginatedSubCategories.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete sub-category");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSubCategory(null);
  }

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={`${parentCategory} : الفئات الفرعية / Sub-Categories`}
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "إدارة الفئات / Categories Management", onClick: () => navigate("/manage-categories") },
          { label: "إدارة الفئات الفرعية / Sub-Categories Management" },
        ]}
      />

      <div className="cards">
        {/* TABLE HEADER */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(val) => {
                setSearchTerm(val);
                setPage(1); // Reset to page 1 on search
              }}
              placeholder={`بحث في الفئات الفرعية لـ / Search Sub-Categories of ${parentCategory}...`}
            />
          </div>
          <div className="right_s">
            <Can module="Categories Management" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedSubCategory(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> إضافة فئة فرعية / Add Sub-Category
              </Button>
            </Can>
          </div>
        </div>

        {/* SUB-CATEGORIES TABLE */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>م / Sr. No.</TableCell>
                <TableCell>أيقونة / Icon</TableCell>
                <TableCell>اسم الفئة الفرعية / Sub-Category Name</TableCell>
                <TableCell>تاريخ الإنشاء / Created Date</TableCell>
                <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                <TableCell>إجراءات / Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedSubCategories.length > 0 ? (
                paginatedSubCategories.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(page - 1) * ROWS_PER_PAGE + index + 1}</TableCell>

                    <TableCell>
                      <div className="user_block">
                        <figure className="no_radius">
                          <img
                            src={row.image || row.icon || "/static/images/placeholder.png"}
                            alt={row.name}
                            onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                          />
                        </figure>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="user_block">
                        <p>
                          <b>{row.name}</b>
                        </p>
                      </div>
                    </TableCell>

                    <TableCell>{moment(row.createdAt).format("DD-MMM-YYYY")}</TableCell>

                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={(e) => handleToggle(row, e.target.checked)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled" : "Disabled"}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Categories Management" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit" arrow>
                            <IconButton onClick={() => handleEdit(row)}>
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Categories Management" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete" arrow>
                            <IconButton onClick={() => handleDeleteClick(row)}>
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    لا توجد فئات فرعية / No Sub-Categories Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Pagination
            module={paginatedSubCategories}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={totalPages}
            totalCount={filteredSubCategories.length}
            setPage={setPage}
          />
        )}
      </div>

      {/* ADD SUB-CATEGORY MODAL */}
      <AddSubCategory
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        parentId={categoryId}
        subCategoryData={selectedSubCategory}
        parentVehicleTypes={categoryData?.data?.type}
      />

      {/* DELETE CONFIRM MODAL */}
      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the sub-category "${selectedSubCategory?.name}"${selectedSubCategory?.name_ar ? ` / "${selectedSubCategory?.name_ar}"` : ""}?`}
      />
    </>
  );
};

export default ManageSubCategories;